import { UrlSegment, UrlMatchResult } from '@angular/router';
import {
    isLocalePrefixSegment,
    normalizeLocaleToken,
} from '../../route-config/domain/entities/seo-locale';
import { isCountryUrlSegment } from '@app/common/locale/domain/entities/locale-url-segment';

/**
 * Matchers para el routing relacionados con locales
 */

/**
 * Matcher que identifica si el primer segmento de la URL es un locale válido.
 *
 * @param segments - Array de segmentos de la URL
 * @returns Objeto con el segmento consumido y parámetros, o `null` si no coincide
 *
 * @example
 * localeRootMatcher([{ path: 'es-BO' }, { path: 'my' }])
 * // retorna { consumed: [{ path: 'es-BO' }], posParams: { langCode: { path: 'es-BO' } } }
 *
 * localeRootMatcher([{ path: 'my' }])
 * // retorna null
 */
export function localeRootMatcher(segments: Array<UrlSegment>): UrlMatchResult | null {
    if (!segments.length) {
        return null;
    }

    const rawSegment: string = segments[0].path;

    // Segmento de pais actual (`/ar`, `/pe`, y a futuro `/ar-en`).
    if (!isCountryUrlSegment(rawSegment)) {
        const firstSegment: string = normalizeLocaleToken(rawSegment);

        if (!isLocalePrefixSegment(firstSegment)) {
            return null;
        }

        // Formato legacy `es-XX` / globales `es`, `en`. Se siguen matcheando
        // para que la app renderice mientras el 301 del server hace su trabajo.
        const localeFamily: string = firstSegment.split('-')[0] ?? '';

        if (localeFamily !== 'es' && localeFamily !== 'en') {
            return null;
        }
    }

    return {
        consumed: [segments[0]],
        posParams: {
            langCode: segments[0],
            lang: segments[0],
        },
    };
}
